import { useDuplicateProductMutation, useGetBrandQuery } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { BrandSelector } from '../../components/selectors/BrandSelector';
import { OrganizationSelector } from '../../components/selectors/OrganizationSelector';

interface DuplicateProductModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	product: {
		id: string;
		name: string;
		organizationId?: string | null;
		organizationName?: string | null;
		brandId?: string | null;
	} | null;
	onSuccess: () => void;
}

export function DuplicateProductModal({
	open,
	onOpenChange,
	product,
	onSuccess,
}: DuplicateProductModalProps) {
	const [name, setName] = useState('');
	const [sku, setSku] = useState('');
	const [organizationId, setOrganizationId] = useState('');
	/* A brand belongs to one client, so it is picked under the target client and
	   cleared when that changes. */
	const [brandId, setBrandId] = useState('');
	const [brandLabel, setBrandLabel] = useState('');
	const [duplicateProduct, { loading }] = useDuplicateProductMutation();

	/* The list only carries the source's brand id; the picker needs its name. */
	const { data: sourceBrand } = useGetBrandQuery({
		variables: { id: product?.brandId ?? '' },
		fetchPolicy: 'no-cache',
		skip: !open || !product?.brandId,
	});
	const sourceBrandName = sourceBrand?.getBrand?.name;

	// Seed the form from the source product each time the modal opens.
	useEffect(() => {
		if (open && product) {
			setName(`${product.name} (Copy)`);
			setSku('');
			setOrganizationId(product.organizationId ?? '');
			setBrandId(product.brandId ?? '');
			setBrandLabel('');
		}
	}, [open, product]);

	const handleDuplicate = async () => {
		if (!product) return;
		try {
			const { data } = await duplicateProduct({
				variables: {
					input: {
						productId: product.id,
						name: name.trim() || undefined,
						sku: sku.trim() || undefined,
						// Empty string clears the org (duplicate becomes an admin product);
						// undefined would fall back to the source product's organization.
						organizationId: organizationId || null,
						/* No client, no brand: a brand only exists under one. */
						brandId: (organizationId && brandId) || null,
					},
				},
			});
			if (!data?.duplicateProduct?.success) {
				toast.error(data?.duplicateProduct?.message || 'Failed to duplicate product');
				return;
			}
			toast.success(`${product.name} duplicated`);
			onSuccess();
			onOpenChange(false);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to duplicate product');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Duplicate product</DialogTitle>
					<DialogDescription>
						Create a copy of {product?.name ?? 'this product'}. Optionally rename it,
						set a SKU, or assign it to a different organization and brand.
					</DialogDescription>
				</DialogHeader>

				<DialogBody className="flex flex-col gap-4">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="duplicate-name">Name</Label>
						<Input
							id="duplicate-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Product name"
							autoFocus
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<Label htmlFor="duplicate-sku">SKU</Label>
						<Input
							id="duplicate-sku"
							value={sku}
							onChange={(e) => setSku(e.target.value)}
							placeholder="Auto-generated when empty"
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<Label>Organization</Label>
						<OrganizationSelector
							value={organizationId}
							onChange={(next) => {
								if (next !== organizationId) {
									setBrandId('');
									setBrandLabel('');
								}
								setOrganizationId(next);
							}}
							selectedLabel={product?.organizationName ?? undefined}
							placeholder="Internal (no client)"
							clearable
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<Label>Brand</Label>
						<BrandSelector
							value={brandId}
							onChange={(next, label) => {
								setBrandId(next);
								setBrandLabel(label ?? '');
							}}
							organizationId={organizationId || undefined}
							disabled={!organizationId}
							clearable
							selectedLabel={
								brandId
									? brandLabel ||
										(brandId === product?.brandId ? sourceBrandName : undefined)
									: undefined
							}
							placeholder={organizationId ? 'No brand' : 'Pick a client first'}
							skip={!open}
						/>
					</div>
				</DialogBody>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button onClick={handleDuplicate} disabled={loading}>
						{loading ? 'Duplicating...' : 'Duplicate'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
